import React, { useMemo, useState } from "react";

import { ColumnType } from "antd/es/table";
import type { FilterValue, TablePaginationConfig } from "antd/es/table/interface";
import capitalize from "lodash/capitalize";

import { getProgramInternalName, useGetAllCarbonPrograms, useGetPartners, useNavigate } from "utils";

import { COUNTRY_CODE } from "consts";

import { ACCOUNTING_METHODOLOGY, AccountingMethodology, CarbonProgram } from "models";

import { Button, Col, Flex, Icon, InputSearch, Link, ModalContent, Row, Table, TablePanel, Tabs, Tooltip, Typography } from "components";

import { ContractsContent } from "pages/admin/carbonPrograms/ContractsContent";
import { FeatureFlagsContent } from "pages/admin/carbonPrograms/FeatureFlagsContent";

type ProgramRow = {
  key: string;
  id: string;
  programName: string;
  partnerName: string;
  methodology: AccountingMethodology;
  country: COUNTRY_CODE;
  farmCount: number;
};

const SettingsModal = ({ carbonProgram }: { carbonProgram: CarbonProgram }) => {
  const programName = getProgramInternalName(carbonProgram);

  const tabItems = [
    {
      label: "Contracts",
      key: "contracts",
      children: <ContractsContent carbonProgram={carbonProgram} />,
    },
    {
      label: "Feature Flags",
      key: "feature-flags",
      children: <FeatureFlagsContent carbonProgram={carbonProgram} />,
    },
  ];

  return (
    <>
      <Typography variant="h3" className="margin-bottom-200">{`Program settings: ${programName}`}</Typography>
      <Tabs items={tabItems} />
    </>
  );
};

export const Overview = () => {
  const navigate = useNavigate();
  const { data: carbonPrograms = [] } = useGetAllCarbonPrograms();
  const partnersQuery = useGetPartners();

  const [isProgramModalOpen, setIsProgramModalOpen] = useState<boolean>(false);
  const [selectedProgram, setSelectedProgram] = useState<CarbonProgram | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [methodologyFilter, setMethodologyFilter] = useState<AccountingMethodology[] | null>(null);
  const [countryFilter, setCountryFilter] = useState<COUNTRY_CODE[] | null>(null);

  const handleClick = (id: string) => {
    setSelectedProgram(carbonPrograms.find((program) => program.id === id) ?? null);
    setIsProgramModalOpen(true);
  };

  const handleModalClose = () => {
    setIsProgramModalOpen(false);
    setSelectedProgram(null);
  };

  const data: ProgramRow[] = carbonPrograms.map((program) => {
    const { id, accountingMethodology, country, farmCount } = program;
    const partner = partnersQuery.data?.partners.find((p) => p.id === program.partnerId);

    return {
      key: id,
      id,
      programName: getProgramInternalName(program),
      partnerName: partner?.name ?? "",
      methodology: accountingMethodology,
      country,
      farmCount,
    };
  });

  const searchedData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return data;
    return data.filter((row) => row.programName.toLowerCase().includes(query) || row.partnerName.toLowerCase().includes(query));
  }, [data, searchQuery]);

  const handleTableChange = (_pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>) => {
    setMethodologyFilter((filters.methodology as AccountingMethodology[] | null) ?? null);
    setCountryFilter((filters.country as COUNTRY_CODE[] | null) ?? null);
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setMethodologyFilter(null);
    setCountryFilter(null);
  };

  const countryFilters = useMemo(
    () =>
      Array.from(new Set(data.map((row) => row.country)))
        .sort((a, b) => a.localeCompare(b))
        .map((country) => ({ text: country, value: country })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carbonPrograms]
  );

  const methodologyFilters = Object.values(ACCOUNTING_METHODOLOGY).map((methodology) => ({
    text: capitalize(methodology),
    value: methodology,
  }));

  const columns: ColumnType<ProgramRow>[] = [
    {
      title: "program name",
      dataIndex: "programName",
      key: "programName",
      className: "paragraph-xs-bold",
      sorter: (a, b) => a.programName.localeCompare(b.programName),
      render: (programName: string) => <TablePanel headline={programName} />,
    },
    {
      title: "partner name",
      dataIndex: "partnerName",
      key: "partnerName",
      className: "paragraph-xs-bold",
      sorter: (a, b) => a.partnerName.localeCompare(b.partnerName),
      render: (partnerName: string) => <Typography variant="paragraph-m">{partnerName}</Typography>,
    },
    {
      title: "methodology",
      dataIndex: "methodology",
      key: "methodology",
      className: "paragraph-xs-bold",
      sorter: (a, b) => a.methodology.localeCompare(b.methodology),
      filters: methodologyFilters,
      filteredValue: methodologyFilter,
      onFilter: (value, record) => record.methodology === value,
      render: (methodology: AccountingMethodology) => <Typography variant="paragraph-m">{capitalize(methodology)}</Typography>,
    },
    {
      title: "country",
      dataIndex: "country",
      key: "country",
      className: "paragraph-xs-bold",
      sorter: (a, b) => a.country.localeCompare(b.country),
      filters: countryFilters,
      filteredValue: countryFilter,
      onFilter: (value, record) => record.country === value,
      render: (country: COUNTRY_CODE) => <Typography variant="paragraph-m">{country}</Typography>,
    },
    {
      title: "farm count",
      dataIndex: "farmCount",
      key: "farmCount",
      className: "paragraph-xs-bold",
      sorter: (a, b) => a.farmCount - b.farmCount,
      render: (farmCount: number) => <Typography variant="paragraph-m">{farmCount}</Typography>,
    },
    {
      title: "settings",
      dataIndex: "id",
      key: "settings",
      className: "paragraph-xs-bold",
      render: (id: string) => (
        <Tooltip title="Program settings">
          <Icon className="pointer" iconName="tabler/settings" onClick={() => handleClick(id)} />
        </Tooltip>
      ),
    },
    {
      title: " ",
      dataIndex: "id",
      key: "details",
      className: "paragraph-xs-bold",
      render: (id: string) => <Link onClick={() => navigate(`/carbon-programs/details/${id}`)}>Details</Link>,
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" className="margin-bottom-150">
        <InputSearch
          className="carbon-programs-overview-search"
          label="Search by program or partner name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Button type="secondary" onClick={handleResetFilters}>
          Reset filters
        </Button>
      </Flex>
      <Row>
        <Col>
          <Table data={searchedData} columns={columns} onChange={handleTableChange} className={"farms"} loading={!carbonPrograms.length} />
        </Col>
      </Row>
      {selectedProgram && (
        <ModalContent visible={isProgramModalOpen} onCancel={handleModalClose}>
          <SettingsModal carbonProgram={selectedProgram} />
        </ModalContent>
      )}
    </>
  );
};
